import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, ThemeProvider } from '@mui/material';
import getTheme from '../../../theme/theme';
import { useNavigate } from 'react-router-dom';
import BasicTable, { Column } from '../../../components/table';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BASEURL, ARROW } from '../../../constants/Common';
import { OrganizationFinal } from '../Userrole';

export interface Directory {
  id: number | null;
  user_id: string | number;
  username: string;
  organization: string;
  email: string;
  password: string;
  createdon: string | Date | null;
  category: string;
  role: string;
  title: string;
  [key: string]: string | number | null | Date | string;
}

const DirectoryPage: React.FC = () => {
  const theme = getTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [userdetails, setUserDetails] = useState<Directory[]>([]);
  const [organizationfinal, setOrganizationFinal] = useState<OrganizationFinal | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const storedAdmindata = localStorage.getItem('userRoleAdmin');
    if (storedAdmindata) {
      setUserRole(JSON.parse(storedAdmindata));
    } else {
      console.log('No data found in localStorage');
    }
  }, []);

  const storedData = localStorage.getItem('orgid');
  let parsedData: any = null;

  if (storedData) {
    parsedData = JSON.parse(storedData);
  } else {
    navigate('/login');
  }

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await axios.get(BASEURL + `/organization-users/${parsedData}/`);
        const usersData = response.data;

        if (usersData && usersData.users) {
          setUserDetails(usersData.users);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    };

    const fetchOrganization = async () => {
      try {
        const response = await axios.get(BASEURL + `/organization/${parsedData}/`, {
          headers: {
            Accept: 'application/json',
          },
        });

        setOrganizationFinal(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsers();
    fetchOrganization();
  }, [parsedData]);

  const isSubscriptionExpired = (status: string | undefined): boolean => {
    return status === 'Expired';
  };

  const columns: Column[] = [
    { id: 'employee_id', label: 'Employee Id', align: 'left' },
    { id: 'username', label: 'Employee Name', align: 'left' },
    { id: 'category', label: 'Category', align: 'left' },
    { id: 'role', label: 'Role', align: 'left' },
    { id: 'title', label: 'Title', align: 'left' },
  ];

  const isUserAdmin = userRole === 'Admin';

  const handleEditUser = (userId: string | number) => {
    localStorage.setItem('directoryViewId', JSON.stringify(userId));
    navigate('/adminedituserdetails', { state: { userId: userId } });
  };

  const rows = userdetails.map((user, index) => ({
    id: index + 1,
    employee_id: user.employee_id,
    username: isUserAdmin && !isSubscriptionExpired(organizationfinal?.organization?.organization_subscription_status) ? (
      <Link to={'/adminedituserdetails'} onClick={() => handleEditUser(user.user_id)}>
        {user.username}
      </Link>
    ) : (
      <Typography>{user.username}</Typography>
    ),
    category: user.user_category,
    role: user.user_role,
    title: user.user_title,
  }));

  const handleCreateNew = () => {
    navigate('/adduser');
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ marginTop: '-60px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='h6' component='div'>
            <Link to={'/useraccount'}>User Account</Link>
            {ARROW} Directory
          </Typography>

          {!loading && (
            <Box sx={{ marginLeft: 'auto' }}>
              <Button
                variant='contained'
                onClick={handleCreateNew}
                disabled={
                  !isUserAdmin ||
                  !(
                    organizationfinal?.organization?.organization_subscription_status === 'Active' &&
                    organizationfinal?.organization?.active_users_count < organizationfinal?.subscription_plan?.users
                  ) ||
                  organizationfinal?.organization?.active_users_count === organizationfinal?.subscription_plan?.users ||
                  isSubscriptionExpired(organizationfinal?.organization?.organization_subscription_status) ||
                  !organizationfinal?.organization?.organization_subscription_status
                }
                sx={{
                  textTransform: 'uppercase',
                  color: theme.palette.common.black,
                  '&:disabled': {
                    color: theme.palette.grey[500],
                  },
                }}
              >
                Create User
              </Button>
            </Box>
          )}
        </Box>

        {!loading && !organizationfinal?.organization?.organization_subscription_status && (
          <Typography variant='h6'>
            Please purchase a subscription plan.
          </Typography>
        )}
        {!loading && organizationfinal?.organization?.active_users_count === organizationfinal?.subscription_plan?.users && (
          <Typography variant='h6'>
            Please upgrade your subscription plan to add more users.
          </Typography>
        )}
        {!loading && isSubscriptionExpired(organizationfinal?.organization?.organization_subscription_status) && (
          <Typography variant='h6'>
            Your subscription plan is expired. Please renew your subscription plan.
          </Typography>
        )}

        <Box sx={{ marginTop: '80px' }}>
          <BasicTable columns={columns} rows={rows} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DirectoryPage;

